import { CommonModule } from '@angular/common';
import {
  Component, EventEmitter, Input, OnInit, Output
} from '@angular/core';
import { ParamMap, RouterModule } from '@angular/router';

import { IconColors, IconSizes, IrisIconModule } from '@sondermind/iris/icon';
import { ProviderPillsComponent } from '@sondermind/features/provider-pills';
import { SessionType, SessionTypeDisplay } from '@sondermind/utilities/models-session';
import { IProviderShareableProfile, StateInfoType } from '@sondermind/utilities/models-public-facing';
import { IRequestSessionParams } from '@sondermind/features/public-facing/session-request';
import { BreakpointsService } from '@sondermind/utilities/services';
import { StringArrayToCommaListPipe } from '@sondermind/utilities/pipes';

import { ITopSpecialty } from '../../models/top-specialty.interface';

const PSYCHIATRIST_EDUCATION_LEVELS = ['MD', 'DO', 'RN'];

export enum ProviderTypes {
  PSYCH = 'Psychiatrist',
  NON_PSYCH = 'Therapist'
}

@Component({
  selector: 'landing-page-header',
  standalone: true,
  imports: [
    CommonModule,
    IrisIconModule,
    RouterModule,
    ProviderPillsComponent,
    StringArrayToCommaListPipe,
  ],
  templateUrl: './landing-page-header.component.html',
  styleUrls: ['./landing-page-header.component.scss']
})
export class LandingPageHeaderComponent implements OnInit {
  @Input() providerShareableProfile!: IProviderShareableProfile;
  @Input() useCheckoutFormFf: boolean = false;
  @Input() showRedesignFf: boolean = true;
  @Input() paramsShareCode: string = '';
  @Input() paramsName: string = '';
  @Input() queryParamMap: ParamMap;
  @Input() requestSessionQueryParams: IRequestSessionParams = null; // For tracking the user coming from .com site
  @Input() topSpecialties: ITopSpecialty[] = [];
  @Output() requestASessionClicked: EventEmitter<void> = new EventEmitter<void>();

  credentialStatesFormatted: string[] = [];
  maxLengthOfInsuranceListString = 56;
  noProfilePhoto: boolean = true;
  providerInsurances: string = '';
  sessionTypeString: string = '';

  queryParamSpecialties: string[] = [];
  providerType: ProviderTypes = ProviderTypes.NON_PSYCH;
  topSpecialtiesIds: number[] = [];
  certificationAbbreviations: string = '';

  iconColor: IconColors = IconColors.BRAND1;
  iconSizes = IconSizes;

  constructor(public breakpointService: BreakpointsService) {}

  ngOnInit(): void {
    this.noProfilePhoto = this.providerShareableProfile.profile_photo_url.endsWith('/missing.png');

    this.credentialStatesFormatted = this.formatCredStates(this.providerShareableProfile.credentialed_states);

    // Functionality to display data in the redesign only
    if (this.showRedesignFf) {
      this.queryParamSpecialties = this.queryParamMap.get('specialties')?.split(',') ?? [];
      this.topSpecialtiesIds = this.providerShareableProfile.top_specialties.map((specialty) => specialty.id);

      this.providerType = this.checkProviderType();
      this.certificationAbbreviations = this.createCertificationAbbreviationList();
    } else {
      // vs. functionality that can be removed after redesign is complete
      this.sessionTypeString = this.formatSessionTypeList(this.providerShareableProfile.session_types_offered);

      this.providerInsurances = this.providerShareableProfile.insurances.join(', ');
    }
  }

  private checkProviderType(): ProviderTypes {
    const isPsychiatrist = PSYCHIATRIST_EDUCATION_LEVELS.includes(this.providerShareableProfile.education_level);
    return isPsychiatrist ? ProviderTypes.PSYCH : ProviderTypes.NON_PSYCH;
  }

  // Since providers can have the same certification in multiple states, don't show any duplicates
  private createCertificationAbbreviationList(): string {
    return [...new Set(this.providerShareableProfile.certifications.map((cert) => cert.abbrev))].join(', ');
  }

  private formatCredStates(states: StateInfoType[]): string[] {
    return states.map((state) => state.name);
  }

  private formatSessionTypeList(sessionTypes: string[]): string {
    const arrayToJoin: string[] = [];

    if (sessionTypes.includes(SessionType.IN_PERSON)) {
      arrayToJoin.push(SessionTypeDisplay.IN_PERSON);
    }
    if (sessionTypes.includes(SessionType.VIDEO)) {
      arrayToJoin.push(SessionTypeDisplay.VIDEO);
    }

    return arrayToJoin.join(', ');
  }

  scrollToElement(elementId: string): void {
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }

  onRequestASessionClick(): void {
    this.requestASessionClicked.emit();
  }
}
